import { auth } from '../utils/firebase';
import { create } from 'zustand';
import { User } from '../types/user';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { userService } from '../services/userService';
import { AppError } from '../utils/errors';
import { useStore } from './useStore';
import { appAccessService } from '../services/appAccessService';



interface AuthStore {
  user: User | null;
  loading: boolean;
  initialized: boolean;
  error: Error | null;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  setError: (error: Error | null) => void;
  reset: () => void;
  logout: () => Promise<void>;
  initializeAuthListener: () => () => void;
}

const AUTH_SESSION_KEY = 'auth_session';

const handleAuthStateChange = async (
  firebaseUser: FirebaseUser | null,
  set: (state: Partial<AuthStore>) => void,
  get: () => AuthStore,
) => {
  try {
    if (firebaseUser) {
      if (!appAccessService.hasPrivateAppAccess(firebaseUser)) {
        const accessError = new AppError(
          'Private app access denied.',
          'PRIVATE_APP_ACCESS',
          403,
        )
        useStore.getState().clearUser()
        set({
          user: null,
          loading: false,
          initialized: true,
          error: accessError,
        })
        await auth.signOut()
        return
      }

      try {
        // Map Firebase user to our User type
        const mappedUser = {
          id: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          displayName: firebaseUser.displayName || '',
          photoURL: firebaseUser.photoURL,
        } as User;

        // Get the current user profile.
        const userData = await userService.getCurrentUser();
        
        if (userData?.id) {
          const migratedUserData = userData;
          
          if (migratedUserData) {
            // Defer initialization to avoid circular dependencies
            setTimeout(() => {
              try {
                // Sync with main store
                useStore.getState().setUser(migratedUserData);
                useStore.getState().setIsInitialized(true);
              } catch (error) {
                console.warn('[AuthStore] Failed to initialize stores:', error);
              }
            }, 0);

            set({ 
              user: migratedUserData,
              loading: false,
              initialized: true,
              error: null 
            });
          } else {
            // Fallback to basic user data if migration fails
            // Sync with main store (deferred)
            setTimeout(() => {
              try {
                useStore.getState().setUser(userData);
                useStore.getState().setIsInitialized(true);
              } catch (error) {
                console.warn('[AuthStore] Failed to sync user data:', error);
              }
            }, 0);
            
            set({ 
              user: userData,
              loading: false,
              initialized: true,
              error: null 
            });
          }
        } else {
          // Sync with main store (deferred)
          setTimeout(() => {
            try {
              useStore.getState().setUser(mappedUser);
              useStore.getState().setIsInitialized(true);
            } catch (error) {
              console.warn('[AuthStore] Failed to sync mapped user:', error);
            }
          }, 0);
          
          set({ 
            user: mappedUser,
            loading: false,
            initialized: true,
            error: null 
          });
        }

      } catch (error) {
        // Handle specific error cases
        if (error instanceof AppError && 
            (error.code === 'USER_NOT_FOUND' || error.code === 'UNAUTHORIZED')) {
          // Set the mapped Firebase user even if profile fetch fails
          const basicUser = {
            id: firebaseUser.uid,
            email: firebaseUser.email,
            emailVerified: firebaseUser.emailVerified,
            displayName: firebaseUser.displayName || '',
            photoURL: firebaseUser.photoURL
          } as User;
          
          // Sync with main store (deferred)
          setTimeout(() => {
            try {
              useStore.getState().setUser(basicUser);
              useStore.getState().setIsInitialized(true);
            } catch (error) {
              console.warn('[AuthStore] Failed to sync basic user:', error);
            }
          }, 0);
          
          set({ 
            user: basicUser,
            loading: false,
            initialized: true,
            error: null 
          });
          return;
        }
        console.error('[AuthStore] Error getting user data:', error);
        // Still set the user but with error state
        const errorUser = {
          id: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          displayName: firebaseUser.displayName || '',
          photoURL: firebaseUser.photoURL
        } as User;
        
        // Sync with main store (deferred)
        setTimeout(() => {
          try {
            useStore.getState().setUser(errorUser);
            useStore.getState().setIsInitialized(true);
            useStore.getState().setError(error as Error);
          } catch (syncError) {
            console.warn('[AuthStore] Failed to sync error user:', syncError);
          }
        }, 0);
        
        set({
          user: errorUser,
          error: error as Error,
          loading: false,
          initialized: true
        });
      }
    } else {
      const currentError = get().error
      const privateAccessError = currentError instanceof AppError
        && currentError.code === 'PRIVATE_APP_ACCESS'

      // Sync with main store on logout (deferred)
      setTimeout(() => {
        try {
          useStore.getState().clearUser();
        } catch (error) {
          console.warn('[AuthStore] Failed to clear user:', error);
        }
      }, 0);
      
      set({ 
        user: null,
        loading: false,
        initialized: true,
        error: privateAccessError ? currentError : null
      });
    }
  } catch (error) {
    console.error('[AuthStore] Error in auth state change:', error);
    // Sync error with main store (deferred)
    setTimeout(() => {
      try {
        useStore.getState().setError(error as Error);
        useStore.getState().setIsInitialized(true);
      } catch (syncError) {
        console.warn('[AuthStore] Failed to sync auth error:', syncError);
      }
    }, 0);
    
    set({
      error: error as Error,
      loading: false,
      initialized: true
    });
  }
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  loading: true,
  initialized: false,
  error: null,
  setUser: (user) => {
    set({ user });
  },
  setLoading: (loading) => set({ loading }),
  setInitialized: (initialized) => {
    set({ initialized });
  },
  setError: (error) => set({ error }),
  reset: () => set({ user: null, loading: false, initialized: false, error: null }),

  initializeAuthListener: () => {
    if (get().initialized) {
      return () => {}
    }

    try {
      const unsubscribe = onAuthStateChanged(auth,
        async (firebaseUser) => {
          await handleAuthStateChange(firebaseUser, set, get)
        },
        (error) => {
          console.error('[AuthStore] Auth state error:', error)
          set({
            user: null,
            error: error as Error,
            loading: false,
            initialized: true
          })
        }
      )

      return unsubscribe
    } catch (error) {
      console.error('[AuthStore] Error initializing auth:', error)
      set({
        user: null,
        error: error as Error,
        loading: false,
        initialized: true
      })
      return () => {}
    }
  },

  logout: async () => {
    try {
      await auth.signOut();
      sessionStorage.removeItem(AUTH_SESSION_KEY);
      
      // Sync with main store (deferred)
      setTimeout(() => {
        try {
          useStore.getState().clearUser();
        } catch (error) {
          console.warn('[AuthStore] Failed to clear user on logout:', error);
        }
      }, 0);
      
      set({ 
        user: null,
        initialized: true,
        loading: false,
        error: null
      });
    } catch (error) {
      set({ error: error as Error });
    }
  }
}));
